import React from "react";
import TopBanner from "../components/TopBanner";
import styled from "styled-components";
import { CTAButton, CTAButtonWrapper } from "../components/HeroSection";
import { Card, Cards, Icon } from "../components/WhoWeAre";
import { CardText, CardTitle } from "../components/WhyChooseUS";

const InsuranceGuidance = ({ openModal }) => {
  React.useEffect(() => {
    document.title =
      "Insurance Guides – Learn Insurance in Simple Terms | Wealthies";
  }, []);

  return (
    <>
      <TopBanner content={"Insurance Basics"} />
      <ContentWrapper>
        <div className="text-content">
          <h2>Introduction</h2>
          <p>
            Insurance is an important part of financial planning, but many
            people find it confusing because of technical terms and complex
            policy details.
          </p>
          <p>
            At Wealthies, our goal is to make insurance easier to understand.
            Our Insurance Guides explain different types of insurance in simple
            language so individuals and families can learn the basics before
            speaking with a licensed insurance professional.
          </p>
          <p>
            These guides help you understand how insurance works, what factors
            to consider before choosing coverage, and how insurance can help
            provide financial protection and potential tax benefits.
          </p>

          <h2>
            Why <span>Understanding</span> Insurance Matters
          </h2>
          <p>
            Before purchasing any insurance policy, it is important to
            understand how the coverage works and what it protects.
          </p>
          <Cards>
            <Card>
              <Icon className="fas fa-book-open"></Icon>
              <CardText>
                Understand different types of insurance policies
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-shield-alt"></Icon>
              <CardText>
                Know what coverage may be suitable for your needs
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-exclamation-triangle"></Icon>
              <CardText>
                Avoid common mistakes people make while buying insurance
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-question-circle"></Icon>
              <CardText>
                Ask the right questions when speaking with an insurance advisor
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-coins"></Icon>
              <CardText>
                Understand potential tax benefits available under Indian tax
                laws
              </CardText>
            </Card>
          </Cards>

          <h2>
            Insurance <span>Guide</span> Categories
          </h2>

          <h4>Term Insurance Guide</h4>
          <p>
            Term insurance is a type of life insurance designed to provide
            financial protection for your family in case of unforeseen events.
          </p>
          <p>
            A term insurance policy provides coverage for a specific period and
            offers financial support to the nominee if the insured person passes
            away during the policy term.
          </p>
          <p>In this guide you will learn:</p>
          <ul>
            <li>What term insurance is</li>
            <li>
              Why many families consider term insurance for financial protection
            </li>
            <li>How term insurance coverage works</li>
            <li>Factors that influence premiums</li>
            <li>Common mistakes people make while choosing a policy</li>
            <li>Tax benefits related to term insurance</li>
          </ul>
          <HyperLinkWrapper>
            <a className="cta-button" href="/term-insurance">
              Read Term Insurance Guide
            </a>
          </HyperLinkWrapper>
          <h4>Health Insurance Guide</h4>
          <p>
            Health insurance helps individuals and families manage medical
            expenses such as hospitalisation, treatments, and certain medical
            procedures.
          </p>
          <p>
            Medical costs can increase unexpectedly, and health insurance helps
            reduce the financial burden by providing coverage for eligible
            medical expenses.
          </p>
          <p>In this guide you will learn:</p>
          <ul>
            <li>What health insurance is</li>
            <li>How medical coverage works</li>
            <li>Types of health insurance plans available</li>
            <li>What hospitalisation coverage typically includes</li>
            <li>Waiting periods and exclusions</li>
            <li>Tax benefits available for health insurance</li>
          </ul>

          <h4>Life Insurance Basics</h4>
          <p>
            Life insurance is designed to provide financial security to
            dependents in case something happens to the policyholder.
          </p>
          <p>
            Different types of life insurance policies are available depending
            on financial goals, protection needs, and long-term planning.
          </p>
          <p>In this guide you will learn:</p>
          <ul>
            <li>What life insurance means</li>
            <li>Different types of life insurance policies</li>
            <li>How life insurance supports financial planning</li>
            <li>Important factors to consider before purchasing coverage</li>
          </ul>
          <HyperLinkWrapper>
            <a className="cta-button" href="/life-insurance">
              Learn Life Insurance Basics
            </a>
          </HyperLinkWrapper>

          <h4>Motor Insurance Guide</h4>
          <p>
            Motor insurance provides financial protection for vehicle owners
            against damage, accidents, theft, or third-party liabilities.
          </p>
          <p>
            Motor insurance is mandatory in India for all registered vehicles
            under the Motor Vehicles Act.
          </p>
          <p>In this guide you will learn:</p>
          <ul>
            <li>What motor insurance is</li>
            <li>Types of motor insurance coverage</li>
            <li>What third-party insurance means</li>
            <li>How claims generally work after an accident</li>
            <li>Important points to check before purchasing coverage</li>
          </ul>
          <HyperLinkWrapper>
            <a className="cta-button" href="/motor-insurance">
              Read Motor Insurance Guide
            </a>
          </HyperLinkWrapper>
          <h2>
            Tax <span>Benefits of Insurance</span> in India
          </h2>
          <p>
            Insurance policies can also provide certain tax benefits under the
            Income Tax Act, subject to applicable rules and conditions.
          </p>
          <p>Some commonly known provisions include:</p>
          <Cards>
            <Card>
              <Icon className="fas fa-piggy-bank"></Icon>
              <CardTitle>Section 80C</CardTitle>
              <CardText>
                Premiums paid for eligible life insurance policies may qualify
                for tax deductions under Section 80C of the Income Tax Act, up
                to the prescribed limit.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-heartbeat"></Icon>
              <CardTitle>Section 80D</CardTitle>
              <CardText>
                Health insurance premiums paid for self, spouse, children, and
                parents may qualify for deductions under Section 80D, subject to
                limits defined by tax regulations.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-file-invoice"></Icon>
              <CardTitle>Section 10(10D)</CardTitle>
              <CardText>
                Under certain conditions, the maturity proceeds or claim
                benefits from life insurance policies may be tax-exempt under
                Section 10(10D) of the Income Tax Act.
              </CardText>
            </Card>
          </Cards>

          <h2>
            How <span>Wealthies</span> Helps
          </h2>
          <p>
            Wealthies aims to simplify insurance awareness by helping
            individuals understand insurance concepts and connect with licensed
            insurance professionals for further assistance.
          </p>
          <p>Through our platform, individuals can:</p>
          <ul>
            <li>Learn insurance concepts in simple language</li>
            <li>Understand key factors before purchasing coverage</li>
            <li>
              Connect with licensed insurance advisors for policy assistance
            </li>
            <li>Receive support guidance through Wealthies Shield</li>
          </ul>

          <h2>
            <span>Need help</span> with insurance?
          </h2>
          <p>
            Still unsure about insurance? Our team can help you understand the
            basics and connect you with licensed insurance professionals who can
            guide you further based on your requirements.
          </p>
          <CTAButtonWrapper>
            <CTAButton onClick={openModal}>
              Get Free Insurance Guidance
            </CTAButton>
          </CTAButtonWrapper>
        </div>
      </ContentWrapper>
    </>
  );
};

export default InsuranceGuidance;

export const HyperLinkWrapper = styled.div`
  font-weight: 600;
  margin-bottom: 20px;
  a {
    /* text-decoration: none; */
    color: #1803ff;
  }
`;

export const ContentWrapper = styled.div`
  display: flex;
  padding: 100px;
  gap: 20px;

  .text-content {
    /* width: 50%; */
    p,
    ul,
    li {
      font-size: 1.1rem;
      line-height: 1.6;
      color: #333;
      text-align: left;
    }
  }

  h3,
  h1,
  h2,
  h4,
  h5,
  h6 {
    color: #0d1b2a;
  }

  h5 {
    color: #415a77;
  }

  h1,
  h2,
  h3 {
    color: #005f73;

    span {
      color: #ffd700;
    }
  }

  @media (max-width: 900px) {
    flex-direction: column;
    padding: 30px;

    .text-content {
      width: 100%;
    }
  }
`;
